import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { listQcReportColumnsApi, listQcReportsApi } from '@/lib/reports-api';
import {
  EMPTY_REPORTS_DRAFT,
  toReportsQuery,
  toggleHiddenColumn,
  visibleColumns as computeVisibleColumns,
  type ReportsSearchDraft,
} from '@/lib/reports-filters';
import { resolveCustomerNameDisplay } from '@/lib/customer-name';
import type { QcReportListItem } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';

// Issue #173 + #207：质检报告聚合页 = 真实 /qc/reports 列表（MANAGER+，按 orgId 隔离）。
// #207 增强：高级搜索（关键词 / 时间范围 / AI 评分范围 / 质检结论 / 处理 / 复核状态）、
// 列显示/隐藏面板（白名单来自 /qc/reports/columns）、默认 20 行/页分页（20/50/100）。
// RBAC 红线：仅 ADMIN/SUPER_ADMIN 列表见完整客户名；MANAGER 见脱敏客户名（后端控制）。

const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const BTN =
  'rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50';
const BTN_PRIMARY =
  'rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50';

const PROCESSING_LABEL: Record<string, string> = {
  PENDING: '待处理',
  PROCESSING: '处理中',
  COMPLETED: '已完成',
  FAILED: '失败',
};
const REVIEW_LABEL: Record<string, string> = {
  PENDING_REVIEW: '待复核',
  IN_REVIEW: '复核中',
  REVIEWED: '已复核',
};
const CALL_STATUS_LABEL: Record<string, string> = {
  PENDING: '待处理',
  PROCESSING: '转写中',
  COMPLETED: '已完成',
  FAILED: '失败',
};
const QC_RESULT_LABEL: Record<string, string> = {
  PASS: '合格',
  FAIL: '不合格',
  PENDING: '待定',
};

const PROCESSING_TONE: Record<string, string> = {
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FAILED: 'bg-rose-50 text-rose-700 border-rose-200',
  PROCESSING: 'bg-sky-50 text-sky-700 border-sky-200',
  PENDING: 'bg-slate-100 text-slate-600 border-slate-200',
};
const REVIEW_TONE: Record<string, string> = {
  REVIEWED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  IN_REVIEW: 'bg-amber-50 text-amber-700 border-amber-200',
  PENDING_REVIEW: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** 列定义：key 与后端 /qc/reports/columns 白名单一致；label 为中文表头。 */
const COLUMN_LABELS: Record<string, string> = {
  employeeName: '员工',
  customerName: '客户',
  startedAt: '通话时间',
  durationSec: '时长',
  callStatus: 'ASR 状态',
  aiScore: 'AI 评分',
  qcResult: '质检结果',
  processingStatus: '处理状态',
  reviewStatus: '复核状态',
};
const DEFAULT_COLUMNS = Object.keys(COLUMN_LABELS);
const PAGE_SIZES = [20, 50, 100];

function tone(map: Record<string, string>, key: string): string {
  return map[key] ?? 'bg-slate-100 text-slate-600 border-slate-200';
}

function Badge({ label, className }: { label: string; className: string }) {
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${className}`}>{label}</span>;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  return err instanceof Error ? err.message : '加载失败，请稍后重试。';
}

export function ReportsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canView = role === 'MANAGER' || role === 'ADMIN' || role === 'SUPER_ADMIN';

  const [draft, setDraft] = useState<ReportsSearchDraft>(EMPTY_REPORTS_DRAFT);
  const [applied, setApplied] = useState<ReportsSearchDraft>(EMPTY_REPORTS_DRAFT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [showColumns, setShowColumns] = useState(false);

  const columnsQuery = useQuery({
    queryKey: ['qc', 'reports', 'columns'],
    enabled: canView,
    queryFn: listQcReportColumnsApi,
  });
  const columns = columnsQuery.data?.columns?.length ? columnsQuery.data.columns : DEFAULT_COLUMNS;
  const visibleColumns = useMemo(() => computeVisibleColumns(columns, hidden), [columns, hidden]);

  const query = useQuery({
    queryKey: ['qc', 'reports', applied, page, pageSize],
    enabled: canView,
    queryFn: () => listQcReportsApi(toReportsQuery(applied, page, pageSize)),
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-6">
        <Header />
        <Card title="质检报告">
          <EmptyState
            title="当前角色无权查看质检报告"
            description="质检报告聚合页仅主管（MANAGER）及以上角色可用。普通员工请使用移动端质检功能。"
          />
        </Card>
      </div>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const show = (key: string) => visibleColumns.includes(key);

  const onSearch = () => {
    setApplied(draft);
    setPage(1);
  };
  const onReset = () => {
    setDraft(EMPTY_REPORTS_DRAFT);
    setApplied(EMPTY_REPORTS_DRAFT);
    setPage(1);
  };
  const toggleColumn = (key: string) => {
    setHidden((prev) => toggleHiddenColumn(prev, key));
  };

  return (
    <div className="flex flex-col gap-6">
      <Header />
      {/* Issue #226：移除页面冗余说明文案，仅保留标题；功能（高级搜索 / 列隐藏 / 分页 / 复核）不变。 */}
      <Card title="质检报告">
        {/* 高级搜索区 */}
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
        >
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">关键词（员工{role !== 'MANAGER' ? ' / 客户名' : ''}）</span>
            <input
              className={INPUT}
              value={draft.keyword}
              placeholder={role !== 'MANAGER' ? '员工姓名 / 精确客户名' : '员工姓名'}
              onChange={(e) => setDraft((d) => ({ ...d, keyword: e.target.value }))}
              data-testid="reports-keyword"
            />
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">开始日期</span>
            <input
              type="date"
              className={INPUT}
              value={draft.startDate}
              onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
            />
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">结束日期</span>
            <input
              type="date"
              className={INPUT}
              value={draft.endDate}
              onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
            />
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">AI 评分</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                className={`${INPUT} w-20`}
                value={draft.minScore}
                placeholder="最低"
                onChange={(e) => setDraft((d) => ({ ...d, minScore: e.target.value }))}
              />
              <span className="text-slate-400">-</span>
              <input
                type="number"
                className={`${INPUT} w-20`}
                value={draft.maxScore}
                placeholder="最高"
                onChange={(e) => setDraft((d) => ({ ...d, maxScore: e.target.value }))}
              />
            </div>
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">质检结果</span>
            <select
              className={INPUT}
              value={draft.qcResult}
              onChange={(e) => setDraft((d) => ({ ...d, qcResult: e.target.value }))}
            >
              <option value="">全部</option>
              <option value="PASS">合格</option>
              <option value="FAIL">不合格</option>
              <option value="PENDING">待定</option>
            </select>
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">处理状态</span>
            <select
              className={INPUT}
              value={draft.processingStatus}
              onChange={(e) => setDraft((d) => ({ ...d, processingStatus: e.target.value }))}
            >
              <option value="">全部</option>
              <option value="PENDING">待处理</option>
              <option value="PROCESSING">处理中</option>
              <option value="COMPLETED">已完成</option>
              <option value="FAILED">失败</option>
            </select>
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            <span className="mb-1">复核状态</span>
            <select
              className={INPUT}
              value={draft.reviewStatus}
              onChange={(e) => setDraft((d) => ({ ...d, reviewStatus: e.target.value }))}
            >
              <option value="">全部</option>
              <option value="PENDING_REVIEW">待复核</option>
              <option value="IN_REVIEW">复核中</option>
              <option value="REVIEWED">已复核</option>
            </select>
          </label>
          <button type="submit" className={BTN_PRIMARY} data-testid="reports-search">
            搜索
          </button>
          <button type="button" className={BTN} onClick={onReset}>
            重置
          </button>
          <div className="relative ml-auto">
            <button
              type="button"
              className={BTN}
              onClick={() => setShowColumns((v) => !v)}
              data-testid="reports-columns-toggle"
            >
              列设置
            </button>
            {showColumns ? (
              <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                {columns.map((key) => (
                  <label key={key} className="flex items-center gap-2 px-1 py-1 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={!hidden.has(key)}
                      onChange={() => toggleColumn(key)}
                      data-testid={`reports-col-${key}`}
                    />
                    {COLUMN_LABELS[key] ?? key}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        </form>

        {query.isLoading ? (
          <LoadingState label="正在加载质检报告…" />
        ) : query.isError ? (
          <ErrorState title="加载质检报告失败" description={errMessage(query.error)} retry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState title="暂无质检报告" description="没有符合当前筛选条件的质检报告。" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  {show('employeeName') ? <th className="py-2 pr-3">员工</th> : null}
                  {show('customerName') ? <th className="py-2 pr-3">客户</th> : null}
                  {show('startedAt') ? <th className="py-2 pr-3">通话时间</th> : null}
                  {show('durationSec') ? <th className="py-2 pr-3">时长</th> : null}
                  {show('callStatus') ? <th className="py-2 pr-3">ASR 状态</th> : null}
                  {show('aiScore') ? <th className="py-2 pr-3">AI 评分</th> : null}
                  {show('qcResult') ? <th className="py-2 pr-3">质检结果</th> : null}
                  {show('processingStatus') ? <th className="py-2 pr-3">处理状态</th> : null}
                  {show('reviewStatus') ? <th className="py-2 pr-3">复核状态</th> : null}
                  <th className="py-2 pr-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r: QcReportListItem) => (
                  <tr key={r.id} className="border-b border-slate-100 align-middle">
                    {show('employeeName') ? (
                      <td className="py-2.5 pr-3 font-medium text-slate-900">{r.employeeName ?? '—'}</td>
                    ) : null}
                    {show('customerName') ? (
                      <td className="py-2.5 pr-3 text-slate-600">{resolveCustomerNameDisplay(r, '—')}</td>
                    ) : null}
                    {show('startedAt') ? (
                      <td className="py-2.5 pr-3 text-slate-600">{formatTime(r.startedAt)}</td>
                    ) : null}
                    {show('durationSec') ? (
                      <td className="py-2.5 pr-3 text-slate-600">{formatDuration(r.durationSec)}</td>
                    ) : null}
                    {show('callStatus') ? (
                      <td className="py-2.5 pr-3">
                        <Badge label={CALL_STATUS_LABEL[r.callStatus] ?? r.callStatus} className={tone(PROCESSING_TONE, r.callStatus)} />
                      </td>
                    ) : null}
                    {show('aiScore') ? (
                      <td className="py-2.5 pr-3 text-slate-700">{r.aiScore != null ? r.aiScore.toFixed(1) : '—'}</td>
                    ) : null}
                    {show('qcResult') ? (
                      <td className="py-2.5 pr-3 text-slate-600">{QC_RESULT_LABEL[r.qcResult] ?? r.qcResult}</td>
                    ) : null}
                    {show('processingStatus') ? (
                      <td className="py-2.5 pr-3">
                        <Badge
                          label={PROCESSING_LABEL[r.processingStatus] ?? r.processingStatus}
                          className={tone(PROCESSING_TONE, r.processingStatus)}
                        />
                      </td>
                    ) : null}
                    {show('reviewStatus') ? (
                      <td className="py-2.5 pr-3">
                        <Badge label={REVIEW_LABEL[r.reviewStatus] ?? r.reviewStatus} className={tone(REVIEW_TONE, r.reviewStatus)} />
                      </td>
                    ) : null}
                    <td className="py-2.5 pr-3 text-right">
                      <Link to={`/calls/${r.callRecordId}`} className="text-sm font-medium text-brand-600 hover:underline">
                        查看详情
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 分页控件：默认 20 行/页，可选 20/50/100。 */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <span>共 {total} 条</span>
                <select
                  className={INPUT}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  data-testid="reports-page-size"
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s} 行/页
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={BTN}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="reports-prev"
                >
                  上一页
                </button>
                <span>
                  第 {page} / {totalPages} 页
                </span>
                <button
                  type="button"
                  className={BTN}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="reports-next"
                >
                  下一页
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Header() {
  return (
    <header>
      <div className="text-xs text-slate-500">质检中心 / 质检报告</div>
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">质检报告</h1>
    </header>
  );
}
